<template>
  <div>
    <div class="drawer-header pb-0">
      <span v-if="!this.isEdit">{{ $t('MDM_NEW') }} {{ $t(sectionTitle) }}</span>
      <span v-else>{{ this.data.name || this.data.short_form }}</span>
      <div class="header-action">
        <Button type="text" size="mini" @click="cancel">
          {{ $t('MDM_CANCEL') }}
        </Button>
        <Button type="primary" size="mini" @click="save">
          {{ isEdit ? $t('MDM_UPDATE') : $t('MDM_CREATE') }}
        </Button>
         <Button v-if="isEdit && showExport" type="primary" size="mini" @click="exportData">
          {{ $t('MDM_EXPORT')}}
        </Button>
        <Button v-if="showUnlock" type="info" size="mini" @click="unlockItem">
          Unlock
        </Button>
      </div>
    </div>
    <div v-if="showLockNote" class="header-statement">
      <div class="status status-red text-sm">{{ $t('MDM_LOCKED_USER_NOTE') }}</div>
    </div>

    <div class="drawer-body drawer-scroll px-20">
      <Form :inline="true" class="form-default" :class="{ 'form-triple': formTriple, 'form-multi': !formTriple }">
        <span v-for="(item, index) in visibleFormConstants" :key="index">
          <span v-if="item.type === 'subColumn'">
            <h5 class="drawer-sub-title">
              {{ item.tlabel ? $t(item.tlabel) : item.title }}
            </h5>
            <FormItem v-for="(subItem, index) in item.subColumns" :key="index"
              :label="subItem.tlabel ? $t(subItem.tlabel) : subItem.title" :required="item.required">
              <Input :placeholder="`Enter ${subItem.tlabel ? $t(subItem.tlabel) : subItem.title
                } here`" :type="subItem.inputType || 'text'" v-model="values[item.paramKey][subItem.paramKey]"
                @input="setValues($event, item.paramKey, subItem.paramKey)" :disabled="subItem.disabled || false">
              </Input>
            </FormItem>
          </span>

          <div v-else-if="item.type === 'break'" class="mt-10"></div>

          <h5 v-else-if="item.type === 'heading'" class="drawer-sub-title">
            {{ item.title }}
          </h5>

          <h5 v-else-if="item.type === 'switch'" class="drawer-sub-title">
            <span>{{ item.tlabel ? $t(item.tlabel) : item.title }}</span>
            <SwitchBtn v-model="values[item.paramKey]" label="right" size="small"
              :disabled="showLockNote || item.disabled" :active-text="$t(item.active_text)"
              :inactive-text="$t(item.inactive_text)" @change="setValues($event, item.paramKey)"
              class="default-switch ml-auto w-75">
            </SwitchBtn>
          </h5>
          <div v-else-if="item.type === 'array'" class="mt-10 table-with-add">
            <Row>
              <div class="add-block" style="width: 100%" @click="pushToArray(item.paramKey)"> Add {{ item.title }}
              </div>
              <Table class="table-striped bordered" ref="tableref" max-height="650px" style="width: 100%"
                empty-text="No data" :data="getTableArray" aria-describedby="FormTable" :stripe="true">
                <table-column :label="$t('MDM_SNO')">
                  <template slot-scope="scope">
                    <span> {{ scope.row.sno }} </span>
                  </template>
                </table-column>
                <table-column :label="item.itemKey">
                  <template slot-scope="scope">
                    <Input class="input-blank p-0" size="mini" type="text" :placeholder="`Enter ${item.itemKey}`"
                      :type="item.inputType || 'text'" v-model="scope.row[item.itemKey]" />
                  </template>
                </table-column>
                <table-column>
                  <template slot-scope="scope">
                    <i class="action-row" @click="deleteRow(scope.row.sno, item.paramKey)">
                      <svgicon name="delete" width="14" height="14" />
                    </i>
                    <Tooltip :open-delay="100" effect="dark" placement="bottom">
                      <div slot="content">{{ $t('MDM_DELETE') }}</div>
                    </Tooltip>
                  </template>
                </table-column>
              </Table>
            </Row>
          </div>
          <FormItem v-else-if="item.type !== 'List'" :label="item.tlabel ? $t(item.tlabel) : item.title"
            :required="item.required">
            <Input v-if="item.type === 'input'" :placeholder="`Enter ${item.tlabel ? $t(item.tlabel) : item.title
              } here`" :type="item.inputType || 'text'" v-model="values[item.paramKey]"
              @input="setValues($event, item.paramKey)" :disabled="item.disabled || false">
            <Select v-if="item.dropdown && getDomains.length > 1 && (!isEdit || !values['email'])" v-model="domain"
              style="width:100px;" slot="append" placeholder="Select">
              <Option v-for="option in getDomains" :key="option" :label="'@' + option" :value="'@' + option"></Option>
            </Select>
            <Input v-else-if="item.dropdown && getDomains.length === 1 && (!isEdit || !values['email'])"
              style="width:100px;" slot="append" :placeholder="'@' + getDomains[0]" :disabled="true"> </Input>
            </Input>
            <Select v-if="item.type === 'dropdown'" v-model="values[item.paramKey]" class="select-default no-bg"
              value-key="id" size="mini" filterable :disabled="item.disabled || (item.options.length <= 0)"
              placeholder="Select" @change="setValues($event, item.paramKey)">
              <Option v-for="option in item.options" :key="option[item.optionKey || 'id']"
                :label="option[item.optionLabel || 'name']" :value="option[item.optionKey || 'id']">
              </Option>
            </Select>

            <MultipleSelectDropdown v-else-if="item.type === 'MultiSelectList'" category="text" :options="item.options"
              :selected="values[item.paramKey]" :clearSelected="true" placeholder="Select options"
              :selectAllOption="false" @updateOption="setValues($event, item.paramKey)" />
            <TimePicker v-else-if="item.type === 'timepicker'" :value="values[item.paramKey]" :placeholder="`Enter ${item.tlabel ? $t(item.tlabel) : item.title
              } here`" format="HH:mm:ss" value-format="HH:mm:ss" @input="setValues($event, item.paramKey)" />
            <DatePicker v-else-if="item.type === 'date'" v-model="values[item.paramKey]"
              type="date" :placeholder="`Select ${item.tlabel ? $t(item.tlabel) : item.title}`"
              format="yyyy-MM-dd" value-format="yyyy-MM-dd" size="mini" class="date-picker-default"
              :disabled="item.disabled || false" @change="setValues($event, item.paramKey)" />
          </FormItem>

          <div class="mt-20" v-if="item.endGroup"></div>

          <div v-if="item.type === 'checkbox'">
            <Checkbox v-for="(opt, index) in item.options" :label="opt.title" :key="index"
              :value="checkboxObj[item.paramKey][opt.title]" @change="radioSelected($event, item.paramKey, opt)">
              {{ opt.title }}
            </Checkbox>
          </div>
          <div class="mt-20" v-if="item.type === 'List' && checkIfSystemUser">
            <Collapse v-model="activeName" accordion class="collapse-slider mx-15">
              <CollapseItem :title="`${item.title} - ${selectedDepotsCount()} selected`" name="DEPOT"
                v-if="item.paramKey === 'depot_ids'">
                <div :key="item.paramKey" class="backdrop-overlay" v-if="showDepotList"></div>
                <div :key="item.paramKey" class="position-relative" :class="{ 'is-focus': showDepotList }"
                  v-click-outside="setDepotShowList">
                  <Input placeholder="Search" type="text" size="mini" class="input-search no-border z-index2"
                    :disabled="!hasUserDepotPermission" v-model="searchDepot" @focus="setDepotFocus" />
                  <div :key="item.paramKey" class="position-relative" v-if="showDepotList">
                    <Card :key="item.paramKey" shadow="never" class="card-default card-list-dropdown no-border">
                      <div class="list-item list-boxed" @click="selectAll(item.paramKey)" :selected="isAllSelected">
                        <span class="icon-user icon-teal">
                          <svgicon name="icon-source" width="12" height="11" />
                        </span>
                        <div class="list-text">
                          <span>{{  $t('MDM_SELECT_DEPOT') }}</span>
                        </div>
                      </div>
                      <div class="list-item list-boxed" @click="unSelectAll(item.paramKey)" :selected="false"
                        v-if="selectedDepotsCountVar > 0">
                        <span class="icon-user icon-teal">
                          <svgicon name="icon-source" width="12" height="11" />
                        </span>
                        <div class="list-text">
                          <span>{{  $t('MDM_UNSELECT_DEPOT')  }}</span>
                        </div>
                      </div>
                      <div class="list-item list-boxed" :selected="listItem.selected"
                        v-for="(listItem, idx) in searchRender(item.paramKey)" :key="idx"
                        @click="selectItem(listItem, item.paramKey)">
                        <span class="icon-user icon-teal">
                          <svgicon name="icon-source" width="12" height="11" />
                        </span>
                        <div class="list-text">
                          <span>{{ listItem.name }}</span>
                          <small class="pt-5">{{ listItem.reference_id }}</small>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
                <Card shadow="never" class="card-default card-list-scroll no-border">
                  <div v-if="listRender">
                    <div v-for="(option, idx) in listRender(item.paramKey)" :key="idx" class="list-item list-boxed">
                      <span class="icon-user icon-teal">
                        <svgicon name="icon-source" width="12" height="11" />
                      </span>
                      <div class="list-text">
                        <span v-if="option.name">{{ option.name }}</span>
                        <small v-if="option[item.key]" class="pt-5">{{
                          option[item.key]
                          }}</small>
                      </div>
                      <span class="icon-action pointer" v-if="hasUserDepotPermission"
                        @click="deleteListItem(option, item)">
                        <svgicon name="icon-delete-md" width="12" height="12" />
                      </span>
                    </div>
                  </div>
                </Card>
              </CollapseItem>
              <CollapseItem :title="`${item.title} - ${selectedVendorDepotsCount()} selected`" name="VENDOR_DEPOT"
                v-if="item.paramKey === 'vendor_depot_ids'">
                <div :key="item.paramKey" class="backdrop-overlay" v-if="showVendorList"></div>
                <div :key="item.paramKey" class="position-relative" :class="{ 'is-focus': showVendorList }"
                  v-click-outside="setVendorShowList">
                  <Input placeholder="Search" type="text" size="mini" class="input-search no-border z-index"
                    :disabled="!hasUserDepotPermission" v-model="searchVendor" @focus="setVendorFocus" />
                  <div :key="item.paramKey" class="position-relative" v-if="showVendorList">
                    <Card :key="item.paramKey" shadow="never" class="card-default card-list-dropdown no-border">
                      <div class="list-item list-boxed" @click="selectAll(item.paramKey)" :selected="isAllSelected">
                        <span class="icon-user icon-teal">
                          <svgicon name="icon-source" width="12" height="11" />
                        </span>
                        <div class="list-text">
                          <span> {{  $t('MDM_SELECT_VENDOR_DEPOT') }} </span>
                        </div>
                      </div>
                      <div class="list-item list-boxed" @click="unSelectAll(item.paramKey)" :selected="false"
                        v-if="selectedDepotsCountVar > 0">
                        <span class="icon-user icon-teal">
                          <svgicon name="icon-source" width="12" height="11" />
                        </span>
                        <div class="list-text">
                          <span>{{  $t('MDM_UNSELECT_VENDOR_DEPOT')   }}</span>
                        </div>
                      </div>
                      <div class="list-item list-boxed" :selected="listItem.selected"
                        v-for="(listItem, idx) in searchRender(item.paramKey)" :key="idx"
                        @click="selectItem(listItem, item.paramKey)">
                        <span class="icon-user icon-teal">
                          <svgicon name="icon-source" width="12" height="11" />
                        </span>
                        <div class="list-text">
                          <span>{{ listItem.name }}</span>
                          <small class="pt-5">{{ listItem.reference_id }}</small>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
                <Card shadow="never" class="card-default card-list-scroll no-border">
                  <div v-if="listRender">
                    <div v-for="(option, idx) in listRender(item.paramKey)" :key="idx" class="list-item list-boxed">
                      <span class="icon-user icon-teal">
                        <svgicon name="icon-source" width="12" height="11" />
                      </span>
                      <div class="list-text">
                        <span v-if="option.name">{{ option.name }}</span>
                        <small v-if="option[item.key]" class="pt-5">{{
                          option[item.key]
                          }}</small>
                      </div>
                      <span class="icon-action pointer" v-if="hasUserDepotPermission"
                        @click="deleteListItem(option, item)">
                        <svgicon name="icon-delete-md" width="12" height="12" />
                      </span>
                    </div>
                  </div>
                </Card>
              </CollapseItem>
            </Collapse>
          </div>
        </span>
      </Form>
    </div>
  </div>
</template>

<script>
import FilterDropdown from '@common/components/FilterDropdown';
import MultipleSelectDropdown from '@common/components/MultipleDropdown';
import { systemRole } from '@/constants/mdm-v2/users';

export default {
  components: {
    FilterDropdown,
    MultipleSelectDropdown,
  },
  props: {
    formConstant: {
      type: Array,
    },
    sectionTitle: {
      type: String,
    },
    isEdit: {
      type: Boolean,
    },
    data: {
      type: Object,
    },
    formTriple: {
      type: Boolean,
      default: false,
    },
    onlyUserDepotPermission: {
      type: Boolean,
      default: false,
    },
    hasUserDepotPermission: {
      type: Boolean,
      default: false,
    },
    showExport: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      values: {},
      freightForwaderKey: 'id',
      list: [],
      searchDepot: '',
      searchVendor: '',
      showDepotList: false,
      showVendorList: false,
      isSelectedAll: false,
      isUnSelectAll: false,
      checkboxObj: {},
      selectedDepotsCountVar: 0,
      systemRole,
      domain: '',
      activeName: ''
    };
  },
  created() {
    if (this.isEdit && this.data) {
      this.formConstant.forEach((val) => {
        if (val.paramKey && val.subColumns && val.subColumns.length) {
          if (val.type === 'subColumn') {
            this.values[val.paramKey] = {};
          }
          val.subColumns.forEach((subColumn) => {
            this.values[val.paramKey][subColumn.paramKey] =
              this.data[subColumn.paramKey] ||
              this.data[val.paramKey][subColumn.paramKey];
          });
        } else if (val.paramKey in this.data) {
          if (val.type === 'switch') {
            this.values[val.paramKey] = !!this.data[val.paramKey];
          } else if (val.type === 'dropdown') {
            const options = this.visibleFormConstants.find((_item) => _item.type === 'dropdown' && _item.paramKey === val.paramKey).options || [];
            this.values[val.paramKey] = (this.data[val.paramKey] && this.data[val.paramKey].name) ? this.data[val.paramKey].name : this.data[val.paramKey];
            if (options.length <= 0) {
              this.values[val.paramKey] = this.data[val.alternateParam] && this.data[val.alternateParam].name;
            }
          } else if (val.paramKey === 'email' && this.getDomains.length && !this.isEdit) {
            this.values[val.paramKey] = this.data[val.paramKey].split('@')[0];
            this.domain = `@${this.data[val.paramKey].split('@')[1]}`;
          } else {
            this.values[val.paramKey] = val.type === 'date' && this.data[val.paramKey]
              ? this.data[val.paramKey].substring(0, 10)
              : this.data[val.paramKey]; // filtering the required values needed for update
          }
          if (val.inputType && !this.data[val.paramKey]) {
            this.values[val.paramKey] = val.defaultValue;
          }
        } else if (val.defaultValue) {
          this.values[val.paramKey] = val.defaultValue;
        }
      });
      if (this.values.depot_ids || this.values.vendor_depot_ids) {
        this.calculateSelectedDepotsCount(this.values.depot_ids, this.values.vendor_depot_ids);
      } else {
        this.calculateSelectedDepotsCount([], []);
      }
    } else {
      this.formConstant.forEach((val) => {
        if (val.defaultValue) {
          this.values[val.paramKey] = val.defaultValue;
        }
        if (val.type === 'subColumn') {
          this.values[val.paramKey] = {};
        }
      });
      if (this.values.depot_ids || this.values.vendor_depot_ids) {
        this.calculateSelectedDepotsCount(this.values.depot_ids, this.values.vendor_depot_ids);
      } else {
        this.calculateSelectedDepotsCount([], []);
      }
    }
    // Re-assign so every key initialised above becomes a reactive property.
    // Keys added by direct assignment to the initially-empty `values` object are
    // NOT reactive, and `$set` won't fix an already-existing key. Controlled
    // components like DatePicker rely on this reactivity to reflect their value.
    this.values = Object.assign({}, this.values);
  },
  computed: {
    showUnlock() {
      return this.$hasFeature('login_lockout') && this.data.is_account_locked && this.$hasPermission('unlock_login_lockout');
    },
    showLockNote() {
      return this.$hasFeature('login_lockout') && this.data.is_account_locked;
    },
    getTableArray() {
      const arrayItem = this.formConstant.find(val => val.type === 'array');
      if (this.values && this.values[arrayItem.paramKey]) {
        this.values[arrayItem.paramKey].forEach((value, index) => {
          // eslint-disable-next-line no-param-reassign
          value.sno = index + 1;
        });
      }
      return this.values[arrayItem.paramKey] || [];
    },
    getDomains() {
      const role = this.formConstant.find(val => val.paramKey === 'role_id');
      const roleId = this.values['role_id'] && this.values['role_id'].toString();
      if (!role || !Array.isArray(role.options) || !roleId) return [];
      const matched = role.options.find(
        option => option && option.id && option.id.toString() === roleId
      );
      return (matched && Array.isArray(matched.domains)) ? matched.domains : [];
      // return role.domains;
    },
    visibleFormConstants() {
      const formConstant = [];
      this.formConstant.filter((val) => !val.hideView).forEach((val) => {
        val.disabled = this.onlyUserDepotPermission || (val.saveOnce && this.isEdit);
        formConstant.push(val);
      });
      return formConstant;
    },
    listRender() {
      return (paramKey) => {
        const list = this.visibleFormConstants.find((val) => val.type === 'List' && val.paramKey === paramKey);
        if (list) {
          const { paramKey } = list;
          return this.values[paramKey];
        }
        return null;
      };
    },
    isAllSelected() {
      return this.isSelectedAll;
    },
    isUnSelectAllSelected() {
      return this.isUnSelectAll;
    },
    searchRender() {
      return (paramKey) => {
        const list = this.visibleFormConstants.find((val) => val.type === 'List' && val.paramKey === paramKey);
        if (list) {
          const { options, paramKey } = list;
          const listData =
            options && options.length
              ? options.map((opt) => {
                const listObj = {
                  id: opt.id,
                  name: opt.name,
                  reference_id: opt.reference_id,
                  selected: false,
                };
                if (
                  this.values[paramKey] &&
                  this.values[paramKey].length &&
                  this.values[paramKey].find((val) => val._id === opt.id)
                ) {
                  listObj.selected = true;
                }
                return listObj;
              })
              : [];
          if(paramKey === 'vendor_depot_ids'){
           return listData.filter((val) => {
              if (val.name.toLowerCase().includes(this.searchVendor.toLowerCase())
                || val.reference_id.toLowerCase().includes(this.searchVendor.toLowerCase())) {
                return val;
              }
            });
          }
          if (paramKey === 'depot_ids') {
            return listData.filter((val) => {
              if (val.name.toLowerCase().includes(this.searchDepot.toLowerCase())
                || val.reference_id.toLowerCase().includes(this.searchDepot.toLowerCase())) {
                return val;
              }
            });
          }
          return listData.sort((a, b) => b.selected - a.selected);
        }
        return [];
      };
    },
    checkIfSelected() {
      return false;
    },
    checkIfSystemUser() {
      if (this.values && this.values.role_id) {
        const role_id = this.values.role_id;
        const list = this.visibleFormConstants.find((val) => val.paramKey === 'role_id');
        if (list) {
          const { options } = list;
          let role = options.find((data) => data.id === role_id.id);
          if (role && role.name === systemRole) {
            return false;
          } else {
            return true;
          }
        }
      }
      return true;
    },
  },
  methods: {
    unlockItem() {
      this.$emit('unlock', this.data.id)
    },
    deleteRow(sno, key) {
      this.values[key] = this.values[key].filter(key => key.sno !== sno);
      this.values = Object.assign({}, this.values);
    },
    pushToArray(paramKey) {
      const emptyData = { sno: 1, pincode: '', location: '' };
      let newArray = [emptyData];
      if (this.values[paramKey]) {
        newArray = [emptyData, ...this.values[paramKey]];
      }
      this.values[paramKey] = newArray;
      this.values = Object.assign({}, this.values);
    },
    setDepotFocus() {
      this.showDepotList = true;
    },
    setVendorFocus() {
      this.showVendorList = true;
    },
    cancel() {
      this.$emit('cancel');
    },
    exportData() {
      this.$emit('export', this.data);
    },
    save() {
      const extractPayload = this.formConstant.filter(
        (constant) => !constant.ignore_field
      );
      const payloadDetails = {};
      extractPayload.forEach((constant) => {
        if (constant.paramKey === 'email' && this.getDomains.length && !this.isEdit) {
          payloadDetails[constant.paramKey] = this.values[constant.paramKey] + this.domain;
        } else {
          payloadDetails[constant.paramKey] = this.values[constant.paramKey];
        }
        if (constant.defaultValue && !payloadDetails[constant.paramKey]) {
          payloadDetails[constant.paramKey] = constant.defaultValue;
        }
      });
      if (this.data && this.isEdit) {
        this.$emit('update', payloadDetails);
      } else {
        this.$emit('add', payloadDetails);
      }
    },
    setValues(data, paramKey, subParamKey = null) {
      if (paramKey === 'role_id') {
        this.domain = '';
      }
      if (subParamKey) {
        this.values[paramKey][subParamKey] = data;
      } else {
        if (!this.isEdit && paramKey === 'email' && this.getDomains.length && data.includes('@')) {
          this.$errorMessage({ message: '@ not allowed' });
        } else {
          this.$set(this.values, paramKey, data);
        }
      }
      if (paramKey === this.freightForwaderKey) {
        const ffOptions = this.formConstant.find(
          (freightForwader) => freightForwader.paramKey === paramKey
        );
        const ffDetails = ffOptions.options.find(
          (freightForwader) => freightForwader.id === this.values[paramKey]
        );
        this.$set(this.values, 'govt_id', ffDetails.govt_id);
        this.$set(this.values, 'status', ffDetails.status || true);
        this.values.admin.admin_name = ffDetails.admin_name;
        this.values.admin.admin_email = ffDetails.admin_email;
        this.values.admin.admin_phone = ffDetails.admin_phone;
      }
    },
    deleteListItem(option, formObj) {
      const allOptions = this.values[formObj.paramKey];
      this.values[formObj.paramKey] = allOptions.reduce((acc, opt) => {
        if (opt._id !== option._id) {
          acc.push(opt);
        }
        return acc;
      }, []);
      this.values = Object.assign({}, this.values);
      let depots = this.values['depot_ids'];
      let vendorDepots = this.values['vendor_depot_ids'];
      this.calculateSelectedDepotsCount(depots || [], vendorDepots || [])
    },
    setDepotShowList() {
      this.showDepotList = false;
    },
    setVendorShowList() {
      this.showVendorList = false;
    },
    selectItem(item, key) {
      if (!item.selected) {
        if (!this.values[key]) {
          this.values[key] = [];
        }
        this.values[key].push({
          _id: item.id,
          name: item.name,
        });
        this.values = Object.assign({}, this.values);
        this.isSelectedAll = this.values[key].every(
          (item) => item.selected === true
        );
        this.isUnSelectAll = this.values[key].every(
          (item) => item.selected === false
        );
      } else {
        this.values[key] = this.values[key].filter((obj) => obj._id != item.id);
        this.values = Object.assign({}, this.values);
      }
      let depots = this.values['depot_ids'];
      let vendorDepots = this.values['vendor_depot_ids'];
      this.calculateSelectedDepotsCount(depots, vendorDepots);
    },
    selectAll(paramKey) {
      const list = this.visibleFormConstants.find((val) => val.type === 'List' && val.paramKey === paramKey);
      if (list) {
        const { options } = list;
        const allData = options.map((opt) => {
          return {
            _id: opt.id,
            name: opt.name,
          };
        });
        this.values[paramKey] = allData;
        this.values = Object.assign({}, this.values);
        this.isSelectedAll = true;
        let depots = this.values['depot_ids'];
        let vendorDepots = this.values['vendor_depot_ids'];
        this.calculateSelectedDepotsCount(depots, vendorDepots);
      }
    },
    unSelectAll(paramKey) {
      const list = this.visibleFormConstants.find((val) => val.type === 'List' && val.paramKey === paramKey);
      if (list) {
        this.values[paramKey] = [];
        this.values = Object.assign({}, this.values);
        this.isUnSelectAll = true;
        this.isSelectedAll = false;
        let depots = this.values['depot_ids'];
        let vendorDepots = this.values['vendor_depot_ids'];
        this.calculateSelectedDepotsCount(depots, vendorDepots);
      }
    },
    radioSelected(event, paramKey, subObj) {
      if (!this.checkboxObj[paramKey]) this.checkboxObj[paramKey] = {};
      this.checkboxObj[paramKey][subObj.title] = event;
      this.checkboxObj = Object.assign({}, this.checkboxObj);
    },
    selectedDepotsCount() {
      return this.selectedDepotsCountVar;
    },
    selectedVendorDepotsCount() {
      return this.selectedVendorDepotsCountVar;
    },
    calculateSelectedDepotsCount(depot, vendorDepot) {
      this.selectedDepotsCountVar = depot.length;
      this.selectedVendorDepotsCountVar = vendorDepot.length;
    },
  },
};
</script>

<style scoped>
.header-statement {
  padding: 15px 20px;

  .text-sm {
    text-transform: initial !important;
    display: block;
  }
}
</style>
